import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";

import { useLanguage } from "@/context/language-context";
import { ArrowUpRight, Download, Mail, Phone } from "lucide-react";
import { useEffect } from "react";
import { useLenis } from "@/components/smooth-scroll";

interface ContactModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function ContactModal({ open, onOpenChange }: ContactModalProps) {
  const { content } = useLanguage();
  const lenis = useLenis();

  useEffect(() => {
    if (open) {
      lenis?.stop();
    } else {
      lenis?.start();
    }
  }, [open, lenis]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        showCloseButton={true}
        className="gap-0 overflow-hidden border-border/50 bg-background/95 p-0 backdrop-blur-xl sm:max-w-[560px]"
      >
        <div className="relative px-8 pt-8 pb-6">
          <div className="absolute top-0 right-0 left-0 h-px bg-linear-to-r from-transparent via-primary/50 to-transparent" />

          <DialogHeader className="gap-3">
            <DialogTitle className="text-2xl font-bold tracking-tight">
              {content.contact.modal_title}
            </DialogTitle>
            <DialogDescription className="text-sm leading-relaxed text-muted-foreground">
              {content.contact.modal_description}
            </DialogDescription>
          </DialogHeader>

          <div className="mt-6 flex flex-col flex-wrap gap-4 sm:mt-8 sm:flex-row">
            <a
              href={`mailto:${content.contact.email}`}
              className="group flex items-center gap-4 rounded-full border border-border/50 bg-secondary/20 px-5 py-2.5 transition-all duration-500 ease-out hover:border-foreground/30 hover:bg-foreground"
            >
              <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full border border-border/50 bg-background transition-transform duration-500 group-hover:scale-110 group-hover:bg-background">
                <Mail className="h-3.5 w-3.5 text-foreground" />
              </div>
              <span className="text-sm font-medium tracking-wide text-foreground transition-colors duration-500 group-hover:text-background">
                {content.contact.email}
              </span>
            </a>

            {content.contact.resume_url && (
              <a
                href={content.contact.resume_url}
                download
                className="group flex items-center gap-4 rounded-full border border-border/50 bg-secondary/20 px-5 py-2.5 transition-all duration-500 ease-out hover:border-foreground/30 hover:bg-foreground"
              >
                <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full border border-border/50 bg-background transition-transform duration-500 group-hover:scale-110 group-hover:bg-background">
                  <Download className="h-3.5 w-3.5 text-foreground" />
                </div>
                <span className="text-sm font-medium tracking-wide text-foreground transition-colors duration-500 group-hover:text-background">
                  {content.contact.download_resume}
                </span>
              </a>
            )}

            {content.contact.phone && (
              <a
                href={`tel:${String(content.contact.phone).replace(/\s+/g, "")}`}
                className="group flex items-center gap-4 rounded-full border border-border/50 bg-secondary/20 px-5 py-2.5 transition-all duration-500 ease-out hover:border-foreground/30 hover:bg-foreground"
              >
                <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full border border-border/50 bg-background transition-transform duration-500 group-hover:scale-110 group-hover:bg-background">
                  <Phone className="h-3.5 w-3.5 text-foreground" />
                </div>
                <span className="text-sm font-medium tracking-wide text-foreground transition-colors duration-500 group-hover:text-background">
                  {content.contact.phone}
                </span>
              </a>
            )}
          </div>

          <div className="mt-6 flex flex-wrap items-center gap-3 sm:mt-8">
            {content.social.items.map((link: { label: string; href: string }) => (
              <div key={link.label}>
                <a
                  href={link.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group relative flex h-10 cursor-pointer items-center justify-center overflow-hidden rounded-full border border-border/50 bg-background px-5 text-foreground transition-all duration-500 hover:border-foreground/30 hover:bg-foreground hover:text-background"
                >
                  <div className="absolute inset-0 flex h-full w-full -translate-x-full -skew-x-13 justify-center group-hover:translate-x-full group-hover:duration-1000">
                    <div className="relative h-full w-4 bg-background/20 dark:bg-background/20" />
                  </div>
                  <span className="relative z-10 flex items-center gap-2 text-xs font-medium tracking-widest uppercase">
                    {link.label}
                    <ArrowUpRight className="h-3 w-3 transition-transform duration-500 group-hover:translate-x-1 group-hover:-translate-y-1" />
                  </span>
                </a>
              </div>
            ))}
          </div>
        </div>

        <div className="absolute right-0 bottom-0 left-0 h-px bg-linear-to-r from-transparent via-primary/50 to-transparent" />
      </DialogContent>
    </Dialog>
  );
}
